import defaultProfile from '~/images/HY_tooth.png';
import HeartBorderedIcon from '~/images/icons/HeartBorderedIcon';
import ratingIcon from '~/images/star_icon.svg';
import shieldCheckIcon from '~/images/shield-check.svg';
import type { IUser } from '~/models/User';
import { Colors } from '~/themes/colors';
import { Center } from '../Center';
import { Column } from '../Column';
import { Row } from '../Row';
import { Text } from '../Text';
import { ZStack } from '../ZStack';
import { BackgroundCheckStatusEnum } from '~/models';
import { getShouldShowBackgroundCheckBadge } from '~/growthbook';

interface IProfessionalHeaderProps {
  professional: IUser;
}

/**
 * ProfessionalHeader Component
 *
 * Displays professional information including profile image, name, rating, and credentials.
 * Shows favorite status with a heart icon and handles default profile image fallback.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for professional profile display in the ProfessionalProfile module.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/ProfessionalProfile/components/ProfessionalHeader
 *
 * If this is truly only used for professional profile display, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <ProfessionalHeader professional={professionalData} />
 * ```
 */
export const ProfessionalHeader = ({ professional }: IProfessionalHeaderProps) => {
  const isFavorite = professional?.isFavorite;

  return (
    <Row
      style={{
        flex: 1,
      }}
    >
      <Center
        data-testid="profile-image-container"
        style={{
          position: 'relative',
          width: 104,
          height: 104,
          borderRadius: 104,
          alignItems: 'center',
          justifyContent: 'center',
          border: `3px solid ${isFavorite ? Colors.favorites_400 : 'transparent'}`,
          padding: 2,
        }}
      >
        {professional?.profile_image ? (
          <img
            src={professional.profile_image}
            style={{
              width: 104,
              height: 104,
              borderRadius: 104,
              objectFit: 'contain',
            }}
            alt="profile_image"
          />
        ) : (
          <img
            src={defaultProfile}
            alt="profile_image"
            style={{
              width: 55,
              height: 55,
              objectFit: 'contain',
            }}
          />
        )}
        {isFavorite && (
          <ZStack top="72%" right={-4}>
            <HeartBorderedIcon />
          </ZStack>
        )}
      </Center>

      <Center style={{ marginLeft: 20, flex: 1, alignItems: 'flex-start', overflow: 'hidden' }}>
        <Column
          flex={1}
          style={{
            flex: 1,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Row
            style={{
              width: '100%',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Text
              bold
              fontSize={20}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {`${professional?.first_name ?? ''} ${professional?.last_name ?? ''}`.trim()}
            </Text>

            <Row alignItems="center" style={{ marginLeft: 10 }}>
              <img
                src={ratingIcon}
                alt="rating"
                style={{
                  width: 20,
                  height: 20,
                  margin: '0px 5px 3px 0px',
                }}
              />
              <Text bold marginRight={5}>
                {professional?.average_rating?.toFixed(1) || '5.0'}
              </Text>
            </Row>
          </Row>

          <Text>
            {professional?.license?.subProfession ?? professional?.license?.profession} Credentials
            Verified
          </Text>
          {professional?.backgroundCheck?.status === BackgroundCheckStatusEnum.Clear &&
            getShouldShowBackgroundCheckBadge() && (
              <Row alignItems="center" gap={4} marginTop={-1}>
                <Text marginTop={1}>Background Checked</Text>
                <img
                  src={shieldCheckIcon}
                  alt="shield-check"
                  style={{
                    width: 24,
                    height: 24,
                    margin: '0px 0px 0px 0px',
                  }}
                />
              </Row>
            )}
        </Column>
      </Center>
    </Row>
  );
};
